// src/hooks/useCueMatcher.ts
import { useMemo, useRef } from "react";
import { buildMatcherFromNotes, normalize } from "../logic/trigger-matcher";
import { CueNote } from "../types";

export function useCueMatcher(notes: CueNote[], {
  pinnedRef,
  activeRef,
  onHit,
  debug = false,
}: {
  pinnedRef: React.MutableRefObject<boolean>;
  activeRef: React.MutableRefObject<string | null>;
  onHit: (id: string) => void;
  debug?: boolean;
}) {
  const matcher = useMemo(() => buildMatcherFromNotes(notes), [notes]);
  const lastFinalTextRef = useRef<string>("");

  function processFinal(text: string, origin: "system" | "mic") {
    if (origin !== "system") return; // guardrail

    const normalized = normalize(text);
    // eslint-disable-next-line no-console
    console.log("[NORMALIZED]", `(${origin})`, normalized);
    if (!normalized) return;
    if (normalized === lastFinalTextRef.current) return;

    let result: any;
    if (debug) {
      const t0 = performance.now();
      result = matcher.match(text, activeRef.current, { preferNewest: true });
      const _lat = performance.now() - t0;
      // eslint-disable-next-line no-console
      console.log(`[LATENCY] ${_lat.toFixed(2)} ms | input="${normalized}" | origin=${origin}`);
    } else {
      result = matcher.match(text, activeRef.current, { preferNewest: true });
    }

    if (result) {
      if (pinnedRef.current) {
        // eslint-disable-next-line no-console
        console.log("[PINNED] ignoring match", result.title);
        return;
      }
      // eslint-disable-next-line no-console
      console.log(`[MATCH] (${origin})`, result.trigger, "->", result.title);

      if (debug) {
        const titleSafe = (result?.title ?? "").toString().replace(/"/g, '\\"');
        // eslint-disable-next-line no-console
        console.log(`[TEST RESULT] outcome=matched | stage=${result.stage} | title="${titleSafe}" | origin=${origin}`);
      }

      try { onHit(result.id); } catch (e) { console.error("[SHOW_CUE] failed", e); }
      lastFinalTextRef.current = normalized;
    } else if (debug) {
      const failedAt = (result as any)?.failed_at ?? "end";
      // eslint-disable-next-line no-console
      console.log(`[TEST RESULT] outcome=miss | failed_at=${failedAt} | origin=${origin}`);
    }
  }

  return { processFinal };
}
